import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ActivityReportService } from '@app/resources/services';
import {
  ActivityRowView,
  ActivityTotals,
  CoalitionMerchantActivityRow,
  LocationActivityRange,
  LocationIncomePoint,
} from '@app/areas/dashboard/models/activity-report.model';
import { computeTotals, toRowViews } from '@app/areas/dashboard/utils/metrics';

/**
 * Area service for the dashboard: fetches the activity report, derives metrics
 * and totals, and holds the view state.
 */
@Injectable()
export class DashboardApiService {
  private readonly activityReportService = inject(ActivityReportService);

  private readonly _rows$ = new BehaviorSubject<ActivityRowView[]>([]);
  private readonly _totals$ = new BehaviorSubject<ActivityTotals | null>(null);
  private readonly _loading$ = new BehaviorSubject<boolean>(false);
  private readonly _error$ = new BehaviorSubject<boolean>(false);

  readonly rows$ = this._rows$.asObservable();
  readonly totals$ = this._totals$.asObservable();
  readonly loading$ = this._loading$.asObservable();
  readonly error$ = this._error$.asObservable();

  refresh(coalitionId: number, from: string, to: string, isFreeTier: boolean): Observable<ActivityRowView[]> {
    this._loading$.next(true);
    this._error$.next(false);

    return this.source(coalitionId, from, to, isFreeTier).pipe(
      map((raw) => toRowViews(raw ?? [])),
      tap((rows) => {
        this.publish(rows);
        this._loading$.next(false);
        if (rows.length === 0) {
          // Distinguish "no data" from "request failed" only at the source level;
          // an actual transport error surfaces via error$ in the error branch below.
        }
      })
    );
  }

  /** Raw transaction points for a merchant (all locations) over [from, to) (UTC ISO), for the drill-down chart. */
  getMerchantIncome(merchantId: number, from: string, to: string): Observable<LocationIncomePoint[]> {
    return this.activityReportService.getMerchantIncome(merchantId, from, to).pipe(
      map((result: any) => {
        if (result === null) {
          return [];
        }
        return Array.isArray(result) ? result : result.$values ?? [];
      })
    );
  }

  /** Earliest/latest transaction timestamps for a merchant (greys out empty drill-down periods). */
  getMerchantActivityRange(merchantId: number): Observable<LocationActivityRange | null> {
    return this.activityReportService.getMerchantActivityRange(merchantId);
  }

  private publish(rows: ActivityRowView[]): void {
    this._rows$.next(rows);
    this._totals$.next(computeTotals(rows));
  }

  private source(
    coalitionId: number,
    from: string,
    to: string,
    isFreeTier: boolean
  ): Observable<CoalitionMerchantActivityRow[] | null> {
    // Real backend returns the .NET collection wrapper; unwrap `$values` if present.
    return this.activityReportService.getCoalitionActivity(coalitionId, from, to, isFreeTier).pipe(
      map((result: any) => {
        if (result === null) {
          this._error$.next(true);
          return [];
        }
        return Array.isArray(result) ? result : result.$values ?? [];
      })
    );
  }
}
